import { Component, HostBinding, ChangeDetectionStrategy, OnDestroy } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router, ActivatedRoute } from "@angular/router";
import { BehaviorSubject, NEVER } from "rxjs";
import { take, filter, switchMap } from "rxjs/operators";

import { WebCallTopicService } from "@onsip/common/services/api/resources/webCallTopic/web-call-topic.service";
import { ModalMaterialComponent } from "@onsip/web/features/shared/components/modal/modal-material.component";
import { TopicStateComponent } from "../topic-state.component";
import { GenericErrorModalComponent } from "../errorModals/genericError/generic-error-modal.component";
import { TranslateService } from "@ngx-translate/core";

import { views } from "../../../../app/phone/views";

import { WebCallTopicError } from "@onsip/common/services/api/apiErrorCodes";

@Component({
  templateUrl: "sayso-topic-view.component.html",
  styleUrls: ["./sayso-topic-view.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})

// eslint-disable-next-line rxjs-angular/prefer-composition
export class SaysoTopicViewComponent extends TopicStateComponent implements OnDestroy {
  @HostBinding("class.onsip-no-grid")
  _dontUse = true;

  views = views;
  deleting = new BehaviorSubject<boolean>(false);

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private translate: TranslateService,
    private webCallTopicService: WebCallTopicService,
    protected route: ActivatedRoute
  ) {
    super(route);
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }

  navigate(view: string) {
    this.router.navigate([view], {
      state: {
        topicTarget: this.target,
        skipLocationChange: true,
        replaceUrl: true
      }
    });
  }

  delete() {
    const modal = this.dialog.open(ModalMaterialComponent, {
      panelClass: ["mat-typography", "onsip-dialog-universal-style"], // global material styles on dialog
      data: {
        title: this.translate.instant("ONSIP_I18N.DELETE_TOPIC"),
        message: this.translate.instant("ONSIP_I18N.DELETING_THIS_TOPIC_IS_PERMANENT"),
        primaryBtnText: this.translate.instant("ONSIP_I18N.DELETE"),
        primaryBtnFlat: true
      }
    });

    this.unsubscriber.add(
      modal
        .afterClosed()
        .pipe(
          take(1),
          switchMap(retObj => {
            if (retObj && retObj.doPrimaryAction) {
              this.deleting.next(true);
              this.webCallTopicService.webCallTopicDelete(this.target);
              return this.webCallTopicService.state.pipe(
                filter(state => !state.loading),
                take(1)
              );
            } else {
              return NEVER;
            }
          })
        )
        .subscribe(state => {
          if (!state.errors.length) {
            this.deleting.next(false);
            this.router.navigate([views.SAYSO_SETTINGS]);
          } else {
            const error = state.errors[0];
            if (error) {
              switch (error.code) {
                case WebCallTopicError.ID_INVALID:
                  // user tried to delete a topic that's already deleted... just continue gracefully
                  this.deleting.next(false);
                  this.router.navigate([views.SAYSO_SETTINGS]);
                  break;
                default:
                  this.dialog.open(GenericErrorModalComponent, {
                    disableClose: true,
                    closeOnNavigation: false,
                    panelClass: ["mat-typography", "onsip-dialog-universal-style"]
                  });
                  break;
              }
            }
          }
        })
    );
  }
}
